import { UseFormReset } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { useAuthUser } from 'auth/hooks';
import { ICliJob } from 'cli/types';
import { AccessPermissions, NotificationPosition } from 'core/enums';
import { useCliJobHandler } from 'core/hooks';
import { FormProvider, useNotificationContext } from 'core/states';
import { goTo } from 'core/utils';
import { useVendorOrderActions } from 'orders/hooks';
import { oldOrderPath, orderPartsPath } from 'orders/routes/routePaths';
import { QuoteOrderPricingType } from 'quotes/enums';
import { useQuoteActions } from 'quotes/hooks';
import { IVendorQuote } from 'quotes/types';

import { CreateVendorOrderFromQuoteFormFields } from './CreateVendorOrderFromQuoteFormFields';
import { createVendorOrderFromQuoteSchema } from './schema';
import { ICreateVendorOrderFromQuoteForm } from './types';

interface IProps {
  isOpen: boolean;
  onClose: () => void;
  quote: IVendorQuote;
  orderPricingType?: QuoteOrderPricingType;
}

export const CreateVendorOrderFromQuoteForm = ({
  isOpen,
  onClose,
  quote,
  orderPricingType = null,
}: IProps) => {
  const { t } = useTranslation();

  const { user } = useAuthUser();
  const navigate = useNavigate();

  const { showSuccessNotification } = useNotificationContext();

  const { handleCliJob } = useCliJobHandler();
  const { createFromQuote } = useVendorOrderActions();
  const { createVendorOrderFromQuote } = useQuoteActions();

  const hasNewOrderAccess = user?.resources?.includes(AccessPermissions.ACCESS_NEW_ORDER);

  const onSubmit =
    (reset: UseFormReset<ICreateVendorOrderFromQuoteForm>) =>
    (data: ICreateVendorOrderFromQuoteForm) => {
      if (!data.agreeTermsAndConditions) {
        return;
      }

      const action = hasNewOrderAccess ? createVendorOrderFromQuote : createFromQuote;

      handleCliJob(
        action,
        {
          quoteId: quote.id,
          omitNoPricing: QuoteOrderPricingType.OMIT === orderPricingType ? 1 : 0,
        },
        {
          cliJobInProgressMessage: t('messageOrderCreateInProgress'),
          onCliJobFinished: (cliJob: ICliJob) => {
            const result = cliJob.result;

            showSuccessNotification(
              { message: t('messageOrderCreateSuccess') },
              NotificationPosition.TOP
            );

            if (hasNewOrderAccess) {
              navigate(orderPartsPath((result as Record<string, IVendorQuote>).order.id));

              return;
            }

            goTo(oldOrderPath(result[0].id));
          },
        }
      );

      handleOnClose(reset);
    };

  const handleOnClose = (reset: UseFormReset<ICreateVendorOrderFromQuoteForm>) => {
    reset({ agreeTermsAndConditions: false });
    onClose();
  };

  return (
    <FormProvider schema={createVendorOrderFromQuoteSchema()}>
      <CreateVendorOrderFromQuoteFormFields
        isOpen={isOpen}
        onClose={handleOnClose}
        onSubmit={onSubmit}
      />
    </FormProvider>
  );
};
